import { RadioGroup, RadioGroupRootProps } from '@chakra-ui/react';
import { forwardRef } from 'react';

export interface MRadioGroupProps extends Omit<
  RadioGroupRootProps,
  'disabled' | 'onChange' | 'onValueChange'
> {
  isDisabled?: boolean;
  onChange?: (value: string) => void;
}

/*
 * v3 has no standalone Radio at all — a radio is a `radioGroup` item, and
 * the group owns the value. This wrapper preserves v2's shape: a `value`/
 * `onChange(value: string)` pair (v2's `RadioGroup.onChange` already called
 * back with just the raw value, not an event, so no signature translation
 * needed beyond the callback's own name — `onValueChange`'s `{ value }`
 * detail unwraps to exactly that). `name`/`value`/`disabled` all belong on
 * `Root` itself (unlike Checkbox/Switch, the group — not a hidden input per
 * item — owns the form-submission value), so this stays a single simple
 * pass-through; see `MRadio` for the per-item half.
 */
export const MRadioGroup = forwardRef<HTMLDivElement, MRadioGroupProps>(
  ({ isDisabled, onChange, ...rest }, ref) => (
    <RadioGroup.Root
      ref={ref}
      disabled={isDisabled}
      onValueChange={({ value }) => {
        if (value != null) {
          onChange?.(value);
        }
      }}
      {...rest}
    />
  ),
);

MRadioGroup.displayName = 'MRadioGroup';

export default MRadioGroup;
